/**
 * @file RecalculateQuantitiesButton.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-11
 * @modified 2026-10-15
 * @description Admin-only slide-out button for the contract pricing pages that
 *              forces a full recompute of the cached PO-derived "ordered /
 *              remaining" quantities (pricing.contract_po_fulfilment). Per-row
 *              refreshes happen automatically on edit / PO-link; this is the
 *              manual "recalculate everything" action (e.g. after a Xero sync).
 */

import { useState } from 'react';
import { API_URL } from '../config';
import { useSlideOutButton } from './SlideOutButton';

function RecalculateQuantitiesButton({ user, onDone, onAlert }) {
  const [loading, setLoading] = useState(false);
  // Measured here, where it mounts (CLAUDE.md), not left to the page.
  useSlideOutButton([loading, !!user?.is_admin]);

  if (!user?.is_admin) return null;

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing/contracts/refresh-fulfilment`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to recalculate');
      onAlert?.({
        isOpen: true,
        message: `Recalculated quantities for ${data.refreshed} contract${data.refreshed !== 1 ? 's' : ''}.`,
        type: 'success'
      });
      if (onDone) await onDone();
    } catch (e) {
      onAlert?.({ isOpen: true, message: `Failed to recalculate quantities: ${e.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        className="btn-slideout"
        onClick={handleRecalculate}
        disabled={loading}
        title="Recalculate ordered / remaining quantities from purchase orders (admin)"
      >
        <div className="icon-wrapper">
          <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
            {loading ? 'progress_activity' : 'calculate'}
          </span>
        </div>
        <span className="btn-label">{loading ? 'Recalculating…' : 'Recalculate Quantities'}</span>
      </button>
    </div>
  );
}

export default RecalculateQuantitiesButton;
